import type { z } from 'zod'
import { httpGet } from '@/shared/api/http'
import {
  cardSchema,
  dashboardStructureSchema,
  type Card,
  type DashboardStructure,
} from './schemas'

const MANAGERIUM_BASE_URL =
  import.meta.env.VITE_MANAGERIUM_BASE_URL ??
  'https://managerium.service.consul'

const manageriumGet = <T extends z.ZodTypeAny>(path: string, schema: T) =>
  httpGet(MANAGERIUM_BASE_URL, path, schema)

/**
 * Okta-protected `GET /v1/dashboards/{id}`: the section/card layout. Calls
 * managerium with the Okta access token as a bearer.
 */
export const getDashboardStructure = (
  dashboardId: number
): Promise<DashboardStructure> =>
  manageriumGet(`/v1/dashboards/${dashboardId}`, dashboardStructureSchema)

export type CardFilters = {
  /** Months to slice charts to; left out, the whole series. */
  periods?: number | undefined
  /** Market ids to keep; left out, every market. */
  markets?: string[] | undefined
}

/**
 * Okta-protected `GET /v1/dashboards/{id}/cards/{cardId}`: one KPI or chart,
 * or an empty card when the market filter leaves nothing to show.
 */
export const getDashboardCard = (
  dashboardId: number,
  cardId: number,
  { periods, markets }: CardFilters = {}
): Promise<Card> => {
  const query = new URLSearchParams()
  if (periods !== undefined) query.set('periods', String(periods))
  markets?.forEach(market => query.append('markets', market))
  const search = query.size > 0 ? `?${query}` : ''
  return manageriumGet(
    `/v1/dashboards/${dashboardId}/cards/${cardId}${search}`,
    cardSchema
  )
}
